/**
 * ==============================================================================
 * Centralized Role Resolution & Default Role Manager
 * ==============================================================================
 * 
 * Provides intelligent, configurable, module-aware default role selection
 * based on user permissions, project accesses, group memberships, and module context.
 *
 * Core Rules:
 * 1. When a user has both INITIALIZER and MAKER roles, MAKER is prioritized as default.
 * 2. If the user has only 1 eligible role for the module, select that role automatically.
 * 3. If multiple roles are eligible, apply the centralized priority order rather than
 *    arbitrarily selecting the first role returned by the API.
 * 4. Stale saved roles (roles the user is no longer assigned or which are ineligible
 *    for the current module) are rejected and replaced by the correct eligible default.
 * 5. If the current active role is already valid and eligible for the module, preserve it
 *    to prevent unnecessary role switching during navigation.
 * 6. Manual role selections by the user are preserved across the session.
 */

import { resolveProjectRoles } from "./UserUtils";

// Canonical representation of Initializer role in backend/localStorage
export const CANON_INITIALIZER = "INTIALIZER";

/**
 * Standard centralized role priority order.
 * MAKER takes precedence over INITIALIZER and other operational roles.
 */
export const DEFAULT_ROLE_PRIORITY = [
  "MAKER",
  "CHECKER",
  "INTIALIZER",
  "SUPERVISOR",
  "QUALITY_ENGINEER",
  "SAFETY_CHECKER",
  "PROJECT_HEAD",
  "PROJECT_MANAGER",
  "MANAGER",
  "ADMIN",
  "SUPERADMIN",
  "SECURITY_GUARD",
];

export const MODULE_NAMES = {
  CHECKLISTS: "CHECKLISTS",
  SAFETY_PERMITS: "SAFETY_PERMITS",
  SAFETY_OBSERVATIONS: "SAFETY_OBSERVATIONS",
  SAFETY: "SAFETY",
  NCR: "NCR",
  MIR_WIR: "MIR_WIR",
  PROJECT_HEAD: "PROJECT_HEAD",
  GUARD: "GUARD",
  GENERAL: "GENERAL",
};

/**
 * Normalizes any role string to uppercase, trimmed canonical form.
 * Handles both "INITIALIZER" and "INTIALIZER" consistently.
 */
export const normalizeRole = (role) => {
  const value = String(role || "")
    .trim()
    .toUpperCase()
    .replace(/[-\s]+/g, "_");

  if (!value) return "";
  if (value === "INITIALIZER" || value === "INITIALISER" || value === "INTIALISER") {
    return CANON_INITIALIZER;
  }
  return value;
};

/**
 * Friendly display label for a role.
 */
export const formatRoleLabel = (role) => {
  const norm = normalizeRole(role);
  switch (norm) {
    case "INTIALIZER":
      return "Initializer";
    case "MAKER":
      return "Maker";
    case "CHECKER":
      return "Checker";
    case "SUPERVISOR":
      return "Supervisor";
    case "QUALITY_ENGINEER":
      return "Quality Engineer";
    case "SAFETY_CHECKER":
      return "Safety Checker";
    case "PROJECT_HEAD":
      return "Project Head";
    case "PROJECT_MANAGER":
      return "Project Manager";
    case "MANAGER":
      return "Manager";
    case "ADMIN":
      return "Admin";
    case "SUPERADMIN":
      return "Super Admin";
    case "SECURITY_GUARD":
      return "Security Guard";
    default:
      return role || "";
  }
};

export const isInitializerRole = (r) => normalizeRole(r) === CANON_INITIALIZER;
export const isMakerRole = (r) => normalizeRole(r) === "MAKER";
export const isCheckerRole = (r) =>
  ["CHECKER", "INSPECTOR", "SAFETY_CHECKER", "QUALITY_ENGINEER"].includes(normalizeRole(r));
export const isSupervisorRole = (r) => normalizeRole(r) === "SUPERVISOR";

/**
 * Storage key helper for project-scoped active roles.
 */
export const getRoleStorageKey = (projectId) => `ACTIVE_ROLE_${projectId}`;

/**
 * Detects the active module from the current URL pathname.
 */
export const detectCurrentModule = (pathname = "") => {
  const path = String(pathname || "").toLowerCase();

  if (path.includes("/permit") || path.includes("/safety/permit")) {
    return MODULE_NAMES.SAFETY_PERMITS;
  }
  if (
    path.includes("/safety/observation") ||
    path.includes("/safety/inspection") ||
    path.includes("/safety/session") ||
    path.includes("/safety/my-session")
  ) {
    return MODULE_NAMES.SAFETY_OBSERVATIONS;
  }
  if (path.startsWith("/safety")) {
    return MODULE_NAMES.SAFETY;
  }
  if (path.startsWith("/ncr")) {
    return MODULE_NAMES.NCR;
  }
  if (path.startsWith("/mir") || path.startsWith("/wir")) {
    return MODULE_NAMES.MIR_WIR;
  }
  if (path.startsWith("/project-head") || path.startsWith("/overview")) {
    return MODULE_NAMES.PROJECT_HEAD;
  }
  if (path.startsWith("/guard")) {
    return MODULE_NAMES.GUARD;
  }

  return MODULE_NAMES.CHECKLISTS;
};

/**
 * Returns the list of eligible roles for a given module.
 * If assignedRoles is provided, returns the intersection of assigned roles and module eligibility.
 */
export const getModuleEligibleRoles = (moduleName, assignedRoles = []) => {
  const normAssigned = (assignedRoles || []).map(normalizeRole).filter(Boolean);

  // Module role whitelists
  let allowed = [];
  switch (moduleName) {
    case MODULE_NAMES.SAFETY_PERMITS:
      // Permits have Maker and Checker approval workflows, not Initializer
      allowed = [
        "MAKER",
        "CHECKER",
        "CONTRACTOR_ENGINEER",
        "CONTRACTOR_EHS",
        "ADL_ENGINEER",
        "ADL_EHS",
        "ADMIN",
        "SUPERADMIN",
      ];
      break;

    case MODULE_NAMES.SAFETY_OBSERVATIONS:
    case MODULE_NAMES.SAFETY:
      allowed = [
        "MAKER",
        "CHECKER",
        "SAFETY_CHECKER",
        "QUALITY_ENGINEER",
        "SUPERVISOR",
        "INTIALIZER",
        "ADMIN",
        "SUPERADMIN",
      ];
      break;

    case MODULE_NAMES.NCR:
      allowed = ["MAKER", "CHECKER", "QUALITY_ENGINEER", "ADMIN", "SUPERADMIN"];
      break;

    case MODULE_NAMES.MIR_WIR:
      allowed = ["MAKER", "CHECKER", "QUALITY_ENGINEER", "ADMIN"];
      break;

    case MODULE_NAMES.PROJECT_HEAD:
      allowed = ["PROJECT_HEAD", "PROJECT_MANAGER", "ADMIN", "SUPERADMIN"];
      break;

    case MODULE_NAMES.GUARD:
      allowed = ["SECURITY_GUARD"];
      break;

    case MODULE_NAMES.CHECKLISTS:
    case MODULE_NAMES.GENERAL:
    default:
      allowed = [
        "MAKER",
        "CHECKER",
        "INTIALIZER",
        "SUPERVISOR",
        "QUALITY_ENGINEER",
        "MANAGER",
        "ADMIN",
        "SUPERADMIN",
        "PROJECT_HEAD",
        "PROJECT_MANAGER",
      ];
      break;
  }

  if (normAssigned.length === 0) {
    return allowed;
  }

  // Intersect assigned roles with allowed roles for the module
  const eligible = normAssigned.filter((r) => allowed.includes(r));

  // If no assigned role strictly matches the whitelist (e.g. custom organizational role),
  // fall back gracefully to the assigned roles so access is not blocked.
  return eligible.length > 0 ? eligible : normAssigned;
};

/**
 * Intelligent default role selection algorithm.
 *
 * @param {Object} options
 * @param {Array<string>} options.assignedRoles - Roles assigned to the user (overall or for current project)
 * @param {string} [options.currentRole] - The currently selected or stored role (if any)
 * @param {string} [options.module] - Module name (or auto-detected from window.location)
 * @param {string|number} [options.projectId] - Active project ID
 * @param {Array<string>} [options.priorityOrder] - Priority order (defaults to DEFAULT_ROLE_PRIORITY)
 * @returns {string} The chosen canonical role string
 */
export function selectDefaultRole({
  assignedRoles = [],
  currentRole = "",
  module = null,
  projectId = null,
  priorityOrder = DEFAULT_ROLE_PRIORITY,
} = {}) {
  const normalizedAssigned = Array.from(
    new Set(
      (assignedRoles || [])
        .map(normalizeRole)
        .filter(Boolean)
    )
  );

  if (normalizedAssigned.length === 0) {
    return normalizeRole(currentRole) || "";
  }

  const currentModule =
    module ||
    (typeof window !== "undefined"
      ? detectCurrentModule(window.location?.pathname || "")
      : MODULE_NAMES.GENERAL);

  const eligibleRoles = getModuleEligibleRoles(currentModule, normalizedAssigned);
  const candidatePool = eligibleRoles.length > 0 ? eligibleRoles : normalizedAssigned;

  // 1. If only one eligible role exists, select it automatically
  if (candidatePool.length === 1) {
    return candidatePool[0];
  }

  // 2. Preserve currentRole if it is non-empty, assigned, and eligible for this module
  //    (Avoids unnecessary role switching if current role is already valid)
  const normCurrent = normalizeRole(currentRole);
  if (normCurrent && candidatePool.includes(normCurrent)) {
    return normCurrent;
  }

  // 3. Centralized Priority Rule:
  //    Evaluate candidates against the priority order (MAKER > CHECKER > INTIALIZER > SUPERVISOR ...)
  for (const pRole of priorityOrder) {
    const normP = normalizeRole(pRole);
    if (candidatePool.includes(normP)) {
      return normP;
    }
  }

  // 4. Fallback: First candidate in pool
  return candidatePool[0];
}

/**
 * Extracts all unique normalized roles assigned to a user across top-level fields,
 * roles arrays, project accesses, and group memberships.
 */
export function extractAllRolesFromUser(userData) {
  const rolesSet = new Set();
  if (!userData) return [];

  // 1. Top-level role
  if (userData.role) {
    const r = normalizeRole(userData.role);
    if (r) rolesSet.add(r);
  }

  // 2. Top-level roles array
  if (Array.isArray(userData.roles)) {
    userData.roles.forEach((item) => {
      const raw = typeof item === "string" ? item : item?.role || item?.name;
      const r = normalizeRole(raw);
      if (r) rolesSet.add(r);
    });
  }

  // 3. Per-access roles
  if (Array.isArray(userData.accesses)) {
    userData.accesses.forEach((access) => {
      if (Array.isArray(access.roles)) {
        access.roles.forEach((item) => {
          const raw = typeof item === "string" ? item : item?.role || item?.name;
          const r = normalizeRole(raw);
          if (r) rolesSet.add(r);
        });
      } else if (access.role) {
        const r = normalizeRole(access.role);
        if (r) rolesSet.add(r);
      }
    });
  }

  // 4. Groups
  if (Array.isArray(userData.groups)) {
    userData.groups.forEach((g) => {
      const raw = typeof g === "string" ? g : g?.name || g?.code;
      const r = normalizeRole(raw);
      if (r) rolesSet.add(r);
    });
  }

  return Array.from(rolesSet);
}

/**
 * Persists the selected role into localStorage and dispatches same-tab broadcast events.
 */
export function persistAndBroadcastRole(role, projectId = null) {
  const normalized = normalizeRole(role);
  if (!normalized) return;

  localStorage.setItem("FLOW_ROLE", normalized);
  localStorage.setItem("ACTIVE_ROLE", normalized);

  const pid = projectId || localStorage.getItem("ACTIVE_PROJECT_ID");
  if (pid && pid !== "null" && pid !== "undefined") {
    localStorage.setItem(getRoleStorageKey(pid), normalized);
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("ACTIVE_ROLE_CHANGED", { detail: { role: normalized } })
    );
    window.dispatchEvent(
      new CustomEvent("FLOW_ROLE_CHANGED", { detail: { role: normalized } })
    );
    window.dispatchEvent(
      new CustomEvent("active-role-changed", { detail: { role: normalized } })
    );
  }
}

/**
 * Applies intelligent default role on login or app restore.
 * When a user has both INITIALIZER and MAKER, MAKER is selected automatically.
 */
export function applyDefaultRoleOnLogin(userData) {
  if (!userData) return null;

  const allRoles = extractAllRolesFromUser(userData);
  if (!allRoles.length) return null;

  const defaultRole = selectDefaultRole({
    assignedRoles: allRoles,
    currentRole: null, // Force initial intelligent default priority on login
    module: MODULE_NAMES.GENERAL,
  });

  if (defaultRole) {
    persistAndBroadcastRole(defaultRole);
  }

  return defaultRole;
}

/**
 * Synchronizes the active role when navigating between modules.
 * If the current role is already eligible for the destination module, preserves it.
 * If ineligible, switches to the highest-priority eligible role for that module.
 */
export function syncRoleOnNavigation(pathname, userData = null, projectId = null) {
  if (!pathname || pathname === "/login" || pathname === "/mobile-login") return;

  const currentRole =
    localStorage.getItem("FLOW_ROLE") ||
    localStorage.getItem("ACTIVE_ROLE") ||
    "";

  let user = userData;
  if (!user) {
    try {
      const raw = localStorage.getItem("USER_DATA");
      if (raw && raw !== "undefined") user = JSON.parse(raw);
    } catch {}
  }

  const pid = projectId || localStorage.getItem("ACTIVE_PROJECT_ID");
  let projectRoles = [];
  if (pid && user) {
    projectRoles = resolveProjectRoles(user, pid);
  }

  const allRoles =
    projectRoles.length > 0 ? projectRoles : extractAllRolesFromUser(user);

  if (!allRoles.length) return;

  const targetModule = detectCurrentModule(pathname);
  const resolvedRole = selectDefaultRole({
    assignedRoles: allRoles,
    currentRole,
    module: targetModule,
    projectId: pid,
  });

  if (resolvedRole && resolvedRole !== normalizeRole(currentRole)) {
    persistAndBroadcastRole(resolvedRole, pid);
  }
}
